import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import {
  Bank,
  Building,
  CheckCircleFill,
  Copy,
  ExclamationTriangleFill,
  Link45deg,
  XCircleFill,
} from "react-bootstrap-icons";
import { ClipboardIcon } from "./Controls/ClipboardIcon";
import { Button } from "./Controls/Button";
import { PayoutOnboardingShell } from "./Controls/PayoutOnboardingShell";
import { MonitorStripeApiApi } from "./shared/api/axios-client";
import { createApiService } from "./shared/apiConfig";

type PageState =
  | "loading"
  | "invalid"
  | "expired"
  | "onboarded"
  | "ready"
  | "link";

function DetailRow( {
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
} ) {
  return (
    <div className="payout-onboarding-detail">
      <div className="payout-onboarding-detail-icon">{icon}</div>
      <div>
        <div className="payout-onboarding-detail-label">{label}</div>
        <div className="payout-onboarding-detail-value">{value}</div>
      </div>
    </div>
  );
}

export default function PayoutOnboardingInvitePage() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>( "loading" );
  const [facilityName, setFacilityName] = useState<string>();
  const [onboardingLink, setOnboardingLink] = useState<string>();
  const [generating, setGenerating] = useState( false );
  const [error, setError] = useState<string>();
  const stripeApi = createApiService( MonitorStripeApiApi );

  useEffect( () => {
    if ( !token ) {
      setPageState( "invalid" );
      return;
    }

    stripeApi
      .monitorStripePayoutOnboardingInviteGet( { token } )
      .then( ( res ) => {
        const status = res.data;
        setFacilityName( status.facilityName ?? undefined );

        if ( status.isAlreadyOnboarded ) {
          setPageState( "onboarded" );
        } else if ( status.isExpired ) {
          setPageState( "expired" );
        } else if ( status.isValid ) {
          setPageState( "ready" );
        } else {
          setPageState( "invalid" );
        }
      } )
      .catch( () => {
        setPageState( "invalid" );
      } );
  }, [token] );

  async function generateLink() {
    if ( !token ) return;
    setGenerating( true );
    setError( undefined );
    try {
      const res = await stripeApi.monitorStripePayoutOnboardingInviteLinkPost( {
        token,
      } );
      if ( res.data.connectLink ) {
        setOnboardingLink( res.data.connectLink );
        setPageState( "link" );
      } else {
        setError( "Could not generate onboarding link." );
      }
    } catch {
      setError(
        "Could not generate onboarding link. The invitation may have expired."
      );
    } finally {
      setGenerating( false );
    }
  }

  if ( pageState === "loading" ) {
    return (
      <PayoutOnboardingShell
        title="Stripe payout setup"
        subtitle="Loading your invitation…"
        icon={<Bank size={24} />}
      >
        <div className="text-center py-4">
          <Spinner animation="border" style={{ color: "#D61F84" }} />
        </div>
      </PayoutOnboardingShell>
    );
  }

  if ( pageState === "invalid" ) {
    return (
      <PayoutOnboardingShell
        title="Invalid link"
        subtitle="This invitation could not be verified."
        icon={<XCircleFill size={24} />}
      >
        <Alert variant="danger" className="mb-0">
          This invitation link is not valid. Please contact your parking operator
          for a new link.
        </Alert>
      </PayoutOnboardingShell>
    );
  }

  if ( pageState === "expired" ) {
    return (
      <PayoutOnboardingShell
        title="Link expired"
        subtitle="This invitation is no longer active."
        icon={<ExclamationTriangleFill size={24} />}
      >
        <Alert variant="warning" className="mb-0">
          This invitation expired after 7 days. Please request a new link from
          your parking operator.
        </Alert>
      </PayoutOnboardingShell>
    );
  }

  if ( pageState === "onboarded" ) {
    return (
      <PayoutOnboardingShell
        title="Already complete"
        subtitle="Your payout account is linked."
        icon={<CheckCircleFill size={24} />}
      >
        <Alert variant="success" className="mb-0">
          This payout account has already been linked with Stripe. No further
          action is needed.
        </Alert>
      </PayoutOnboardingShell>
    );
  }

  return (
    <PayoutOnboardingShell
      title="Stripe payout setup"
      subtitle="Generate a secure link to finish connecting your account."
      icon={<Bank size={24} />}
    >
      <div className="mb-4">
        {facilityName && (
          <DetailRow
            icon={<Building size={18} />}
            label="Facility"
            value={facilityName}
          />
        )}
      </div>

      {pageState === "ready" && (
        <>
          <p className="text-muted small mb-3">
            Click below to generate a one-time link to complete setup in Stripe.
            You&apos;ll be redirected to Stripe to verify your payout details.
          </p>
          {error && <Alert variant="danger">{error}</Alert>}
          <Button
            className="btn-main px-4"
            onClick={generateLink}
            loading={generating}
            loadingText="Generating…"
          >
            Generate onboarding link
          </Button>
        </>
      )}

      {pageState === "link" && onboardingLink && (
        <div className="payout-onboarding-link-box">
          <div className="d-flex align-items-center gap-2 mb-3">
            <Link45deg size={20} className="text-primary flex-shrink-0" />
            <div>
              <div className="fw-semibold">Your link is ready</div>
              <div className="text-muted small">
                One-time link · expires in 5 minutes
              </div>
            </div>
          </div>

          <div className="input-group mb-3">
            <input
              type="text"
              className="form-control"
              value={onboardingLink}
              readOnly
              onFocus={( e ) => e.target.select()}
            />
            <span className="input-group-text">
              <ClipboardIcon
                payload={onboardingLink}
                tooltipText="Link copied!"
                icon={<Copy className="small" />}
              />
            </span>
          </div>

          <p className="text-muted small mb-3">
            If the link expires before you finish, refresh the page and generate a new
            one.
          </p>

          <Button
            className="btn-main px-4"
            onClick={() => window.open( onboardingLink, "_blank", "noopener,noreferrer" )}
          >
            Go to Stripe
          </Button>
        </div>
      )}
    </PayoutOnboardingShell>
  );
}
